import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react/hooks';
import { gql, MediaPlayerError } from '@yi/core';

import { useFeatureFlag } from '~/src/components/hooks/useFeatureFlag';

import { useReportMediaPlaybackError } from './logger';
import { useMediaPlayer } from './MediaPlayerProvider';

function useReportNetworkErrors(error: unknown) {
  const report = useReportMediaPlaybackError();
  useEffect(() => {
    if (error) {
      report(new MediaPlayerError(JSON.stringify(error)));
    }
  }, [error, report]);
}

function parse(
  rawData: gql.typesClient.Query_BrightcoveMedia,
  report: (errorDetails: Error | string) => void,
) {
  try {
    const encodedData = rawData.BrightcoveMedia.test;
    const data = JSON.parse(encodedData);
    return data;
  } catch (e) {
    if (typeof e === 'string' || e instanceof Error) {
      report(e);
    }
    return null;
  }
}

function isErrorsList(data: any) {
  return Array.isArray(data) && data.some(item => item.error_code);
}

export const useMediaSourceDetails = (): Record<string, any> => {
  const report = useReportMediaPlaybackError();
  const { mediaId } = useMediaPlayer();

  const { enabled: useByteRange } = useFeatureFlag('brightcove_hls_byterange');
  const { data, error } = useQuery<
    gql.typesClient.Query_BrightcoveMedia,
    gql.typesClient.Query_BrightcoveMediaVariables
  >(gql.Query_BrightcoveMedia, {
    ssr: false,
    variables: {
      brightcoveId: mediaId,
      useByteRange,
    },
  });

  useReportNetworkErrors(error);

  const mediaDetails = useMemo(() => {
    if (data) {
      const parsedData = parse(data, report);
      if (isErrorsList(parsedData)) {
        report(new MediaPlayerError(JSON.stringify(parsedData)));
        return null;
      }
      return parsedData;
    }
    return null;
  }, [data, report]);
  return mediaDetails;
};
